import { cssVar, fxConfig } from './config';
import { getLayer } from './layer';
import { sound } from './sound';

/*
 * Canvas particles: fireworks (rockets → 70 sparks) and confetti (paper with sway/flip).
 * Port of design/lobby/fx.js. Under reduced motion nothing is spawned.
 */
interface Rocket {
  k: 'rocket';
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  col: string;
}
interface Spark {
  k: 'spark';
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  col: string;
}
interface Conf {
  k: 'conf';
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  vr: number;
  ph: number;
  life: number;
  col: string;
}
type Particle = Rocket | Spark | Conf;

let cv: HTMLCanvasElement | null = null;
let parts: Particle[] = [];
let running = false;
let timers: ReturnType<typeof setTimeout>[] = [];

function palette(): string[] {
  if (fxConfig.festive) {
    return [cssVar('--red', '#E5484D'), cssVar('--gold', '#FFD166'), cssVar('--orange', '#FF8A3D'), cssVar('--gold', '#FFD166')];
  }
  return [
    cssVar('--teal', '#2EE6C9'),
    cssVar('--orange', '#FF8A3D'),
    cssVar('--gold', '#FFD166'),
    cssVar('--blue', '#5B7CFA'),
    cssVar('--pink', '#F07AA8'),
    cssVar('--green', '#7BE07B'),
  ];
}

/** Current particle colours (tests, debugging). */
export const particlePalette = palette;

function canvas(): HTMLCanvasElement {
  if (cv && cv.isConnected) return cv;
  cv = document.createElement('canvas');
  cv.dataset.fxCanvas = '';
  cv.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;';
  getLayer().appendChild(cv);
  const fit = () => {
    if (!cv) return;
    cv.width = innerWidth * devicePixelRatio;
    cv.height = innerHeight * devicePixelRatio;
  };
  fit();
  addEventListener('resize', fit);
  return cv;
}

function explode(x: number, y: number, col: string): void {
  const light = cssVar('--text', '#E4EAF4');
  for (let i = 0; i < 70; i++) {
    const a = Math.random() * Math.PI * 2;
    const v = 1.5 + Math.random() * 4.5;
    const life = 50 + Math.random() * 40;
    parts.push({ k: 'spark', x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life, max: life, col: i % 3 === 1 ? light : col });
  }
  sound.play('boom');
}

function run(): void {
  if (running) return;
  const c = canvas();
  const g = c.getContext('2d');
  if (!g) {
    parts = [];
    return;
  }
  running = true;
  const dpr = devicePixelRatio;
  const frame = () => {
    g.clearRect(0, 0, c.width, c.height);
    for (let i = parts.length - 1; i >= 0; i--) {
      const p = parts[i];
      if (!p) continue;
      p.life--;
      if (p.k === 'rocket') {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.12;
        g.strokeStyle = p.col;
        g.lineWidth = 2 * dpr;
        g.beginPath();
        g.moveTo(p.x * dpr, p.y * dpr);
        g.lineTo((p.x - p.vx * 3) * dpr, (p.y - p.vy * 3) * dpr);
        g.stroke();
        if (p.vy >= -1 || p.life <= 0) {
          parts.splice(i, 1);
          explode(p.x, p.y, p.col);
        }
        continue;
      }
      if (p.k === 'spark') {
        const px = p.x;
        const py = p.y;
        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.97;
        p.vy = p.vy * 0.97 + 0.06;
        g.globalAlpha = Math.max(0, p.life / p.max);
        g.strokeStyle = p.col;
        g.lineWidth = 2.2 * dpr;
        g.beginPath();
        g.moveTo(px * dpr, py * dpr);
        g.lineTo(p.x * dpr, p.y * dpr);
        g.stroke();
        g.globalAlpha = 1;
      } else {
        p.x += p.vx + Math.sin(p.life / 9 + p.ph) * 0.8;
        p.y += p.vy;
        p.vy = Math.min(p.vy + 0.05, 3.2);
        p.rot += p.vr;
        g.save();
        g.translate(p.x * dpr, p.y * dpr);
        g.rotate(p.rot);
        g.scale(1, Math.cos(p.life / 6 + p.ph));
        g.fillStyle = p.col;
        g.fillRect(-4 * dpr, -2.5 * dpr, 8 * dpr, 5 * dpr);
        g.restore();
      }
      if (p.life <= 0 || p.y > innerHeight + 40) parts.splice(i, 1);
    }
    if (parts.length) requestAnimationFrame(frame);
    else {
      running = false;
      g.clearRect(0, 0, c.width, c.height);
    }
  };
  requestAnimationFrame(frame);
}

/** n rockets (default 5) bursting over `el` (or the viewport). No-op under reduced motion. */
export function fireworks(n = 5, el?: Element | null): void {
  if (fxConfig.reduced) return;
  let cx = innerWidth / 2;
  let top = innerHeight * 0.3;
  let w = innerWidth * 0.6;
  if (el) {
    const b = el.getBoundingClientRect();
    cx = b.left + b.width / 2;
    top = b.top + b.height * 0.3;
    w = Math.max(Math.min(b.width, innerWidth), 300);
  }
  const pal = palette();
  for (let i = 0; i < n; i++) {
    timers.push(
      setTimeout(() => {
        if (fxConfig.reduced) return;
        const x = cx + (Math.random() - 0.5) * w;
        const ty = top + (Math.random() - 0.5) * 120;
        const y0 = Math.min(innerHeight + 10, ty + 420);
        parts.push({
          k: 'rocket',
          x,
          y: y0,
          vx: (Math.random() - 0.5) * 1.5,
          vy: -Math.sqrt(2 * 0.12 * Math.max(60, y0 - ty)),
          life: 120,
          col: pal[Math.floor(Math.random() * pal.length)] ?? pal[0] ?? '#2EE6C9',
        });
        // a soft pop per rocket — not `launch`, which is the recorded Liftoff take-off
        sound.play('common/pop', { volume: 0.5 });
        run();
      }, i * 260),
    );
  }
}

/** 160 confetti pieces over `el` (or the whole width). No-op under reduced motion. */
export function confetti(el?: Element | null): void {
  if (fxConfig.reduced) return;
  let x0 = 0;
  let w = innerWidth;
  if (el) {
    const b = el.getBoundingClientRect();
    x0 = Math.max(0, b.left - 60);
    w = Math.min(innerWidth, b.width + 120);
  }
  const pal = palette();
  for (let i = 0; i < 160; i++) {
    parts.push({
      k: 'conf',
      x: x0 + Math.random() * w,
      y: -20 - Math.random() * 300,
      vx: (Math.random() - 0.5) * 1.2,
      vy: 1 + Math.random() * 2,
      rot: Math.random() * 6,
      vr: (Math.random() - 0.5) * 0.2,
      ph: Math.random() * 6,
      life: 600,
      col: pal[i % pal.length] ?? '#2EE6C9',
    });
  }
  run();
}

/** Stop all particles immediately (celebration skipped). */
export function clearParticles(): void {
  for (const t of timers) clearTimeout(t);
  timers = [];
  parts = [];
}

/** For tests / debug overlays. */
export function particleCount(): number {
  return parts.length;
}
